import { InputSummaryItem } from '../../../../input-summary';
import InputSummaryUtils from '../../../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock bmp potential elemental model
 */
export interface FeedstockBmpPotentialElementalInterface {
  c: UnitValue;
  h: UnitValue;
  n: UnitValue;
  s: UnitValue;
  o: UnitValue;
  biodegradability: UnitValue;
}

/**
 * A class that contains members for the feedstock BMP potential elemental model
 */
export class FeedstockBmpPotentialElemental {
  c: UnitValue;
  h: UnitValue;
  n: UnitValue;
  s: UnitValue;
  o: UnitValue;
  biodegradability: UnitValue;

  /**
   * constructs the FeedstockBmpPotentialElemental class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockBmpPotentialElementalInterface = {
    c: new UnitValue(),
    h: new UnitValue(),
    n: new UnitValue(),
    s: new UnitValue(),
    o: new UnitValue(),
    biodegradability: new UnitValue(),
  }) {
    this.c = new UnitValue(param.c);
    this.h = new UnitValue(param.h);
    this.n = new UnitValue(param.n);
    this.s = new UnitValue(param.s);
    this.o = new UnitValue(param.o);
    this.biodegradability = new UnitValue(param.biodegradability);
  }

  /**
   * returns a separate instance of the provided FeedstockBmpPotentialElemental obj
   * @param feedstockBmpPotentialElemental will be returned as a separate instance
   */
  static deepCopy(feedstockBmpPotentialElemental: FeedstockBmpPotentialElemental) {
    let copy;
    if (feedstockBmpPotentialElemental) {
      copy = new FeedstockBmpPotentialElemental(feedstockBmpPotentialElemental);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockBmpPotentialElemental');
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockBmpPotentialElemental class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.biodegradability, this.biodegradability, [...path, 'biodegradability']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.c, this.c, [...path, 'c']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.h, this.h, [...path, 'h']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.n, this.n, [...path, 'n']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.s, this.s, [...path, 's']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockBmpPotentialElementalPropName.o, this.o, [...path, 'o']);
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockBmpPotentialElemental class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockBmpPotentialElementalPropName {
  c = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.CARBON',
  h = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.HYDROGEN',
  n = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.NITROGEN',
  s = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.SULFUR',
  o = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.ELEMENTAL.OXYGEN',
  biodegradability = 'INPUT_SUMMARY.FEEDSTOCK.BMP_POTENTIAL.BIODEGRADABILITY',
  id_c = 'carbon',
  id_h = 'hydrogen',
  id_n = 'nitrogen',
  id_s = 'sulfur',
  id_o = 'oxygen',
  id_biodegradability = 'biodegradability'
}
